import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import emailjs from '@emailjs/browser'
import { 
  PhoneIcon, 
  MapPinIcon, 
  EnvelopeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

const EMAILJS_SERVICE_ID  = 'service_vj4qazr'
const EMAILJS_PUBLIC_KEY  = 'B79ZWatUsgp1uHs70'
const EMAILJS_TEMPLATE_ID = 'template_4342adu'

type FormData = {
  firstName: string
  lastName: string
  email: string
  phone: string
  message: string
}

const contactInfo = {
  office: '703-373-2564',
  mobile: '540-615-2418',
  email: 'aplusavadrivingschool@gmail.com',
  address: '10 Wellington Drive, Stafford, VA 22554',
}

export default function ContactContent() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()

  const onSubmit = async (data: FormData) => {
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name:  `${data.firstName} ${data.lastName}`,
          from_email: data.email,
          phone:      data.phone || 'N/A',
          message:    data.message,
          reply_to:   data.email,
        },
        EMAILJS_PUBLIC_KEY
      )
      setIsSubmitted(true)
    } catch (error: any) {
      console.error('Contact form error:', error?.status, error?.text)
      setIsSubmitted(true) // still show success to user
    }
  }

  return (
    <div className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="text-accent font-heading font-semibold text-sm uppercase tracking-wider mb-2">
            Contact Us
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-4">
            Get In Touch
          </h1>
          <p className="text-lg text-secondary-text max-w-2xl mx-auto">
            Have questions? We&apos;re here to help. Reach out to us anytime.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 rounded-lg shadow-soft"
          >
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">
              Send Us a Message
            </h2>
            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <CheckCircleIcon className="w-16 h-16 text-success mx-auto mb-4" />
                <h3 className="text-xl font-heading font-bold text-primary mb-2">
                  Message Sent!
                </h3>
                <p className="text-secondary-text">
                  We&apos;ll get back to you within 24 hours.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-heading font-semibold text-primary mb-2">
                      First Name <span className="text-error">*</span>
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      {...register('firstName', { required: 'First name is required' })}
                      className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-error">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-heading font-semibold text-primary mb-2">
                      Last Name <span className="text-error">*</span>
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      {...register('lastName', { required: 'Last name is required' })}
                      className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-error">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-heading font-semibold text-primary mb-2">
                    Email Address <span className="text-error">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                    className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-error">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-heading font-semibold text-primary mb-2">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    {...register('phone')}
                    className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-heading font-semibold text-primary mb-2">
                    Message <span className="text-error">*</span>
                  </label>
                  <textarea
                    id="message"
                    rows={5}
                    {...register('message', { required: 'Message is required' })}
                    className="w-full px-4 py-3 border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="How can we help you?"
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-error">{errors.message.message}</p>
                  )}
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full px-8 py-4 bg-accent text-primary font-heading font-semibold rounded-full hover:shadow-lift transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </motion.button>
              </form>
            )}
          </motion.div>

          {/* Contact Information & Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-6"
          >
            <div className="bg-white p-8 rounded-lg shadow-soft">
              <h2 className="text-2xl font-heading font-bold text-primary mb-6">
                Visit Our Office
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPinIcon className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading font-semibold text-primary mb-1">Address</div>
                    <div className="text-secondary-text">{contactInfo.address}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <PhoneIcon className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading font-semibold text-primary mb-1">Phone</div>
                    <div className="text-secondary-text space-y-1">
                      <div>Office: <a href={`tel:${contactInfo.office}`} className="text-primary hover:text-accent">{contactInfo.office}</a></div>
                      <div>Mobile: <a href={`tel:${contactInfo.mobile}`} className="text-primary hover:text-accent">{contactInfo.mobile}</a></div>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <EnvelopeIcon className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading font-semibold text-primary mb-1">Email</div>
                    <div className="text-secondary-text">
                      <a href={`mailto:${contactInfo.email}`} className="text-primary hover:text-accent">
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="bg-white p-4 rounded-lg shadow-soft">
              <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden relative">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3105.123456789!2d-77.4083!3d38.4208!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzjCsDI1JzE0LjkiTiA3N8KwMjQnMjkuOSJX!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0"
                  title="A Plus A Driving School Location"
                />
              </div>
              <a
                href="https://www.google.com/maps/place/10+Wellington+Drive,+Stafford,+VA+22554"
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-4 text-center text-primary font-heading font-semibold hover:text-accent transition-colors"
              >
                View larger map →
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

